import { useState } from "react"
import { App as AntdApp, Alert, Button, Form, Input, InputNumber, Modal, Select, TimePicker, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import { RefreshCw } from "lucide-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { RECOMMENDED_TENANT_SECRET_LENGTH, type TenantSummary } from "@csbi/contracts"
import { createTenant, updateTenant } from "../../features/tiles/api.js"
import { ApiError } from "../../shared/api/request.js"
import { ENV_LABELS, ENV_ORDER } from "../../shared/env-labels.js"

const DEFAULT_POLL_CRON = "17 3 * * *"
const DEFAULT_TIMEOUT_MS = 30_000

const DAILY = /^(\d{1,2}) (\d{1,2}) \* \* \*$/

const cronToTime = (expr: string): Dayjs | null => {
  const m = DAILY.exec(expr.trim())
  if (!m) return null
  const [, minute, hour] = m
  const h = Number(hour)
  const min = Number(minute)
  if (h > 23 || min > 59) return null
  return dayjs().hour(h).minute(min).second(0)
}

const timeToCron = (t: Dayjs) => `${t.minute()} ${t.hour()} * * *`

function localHint(t: Dayjs): string {
  const offsetMin = -new Date().getTimezoneOffset()
  if (offsetMin === 0) return ""
  const local = t.add(offsetMin, "minute")
  const dayShift = local.date() === t.date() ? "" : local.isAfter(t) ? " next day" : " previous day"
  return `${local.format("HH:mm")}${dayShift} where you are`
}

/**
 * ⚠ **The secret originates on the srv side; we do not issue it.** Checked against srv's code
 * (`apps/regdesk-server/config/index.js:49`): `SHARED_SECRET` existed before CS-BI did, and more than one
 * consumer uses it —
 */
function suggestSecret(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return btoa(String.fromCharCode(...bytes)).replace(/=+$/, "")
}

export type DeploymentFormProps = {
  open: boolean
  tenant?: TenantSummary | null
  onClose: () => void
}

type Values = {
  id: string
  displayName: string
  environment: string
  baseUrl: string
  secret?: string
  /** Used for the daily shape; otherwise the raw pollCron is used */
  dailyTime?: Dayjs
  pollCron?: string
  requestTimeoutMs?: number
  notes?: string
}

/**
 * ⚠ Every opening uses **a new key so the whole form remounts**, rather than resetting with setState in an effect.
 */
export function DeploymentForm(props: DeploymentFormProps) {
  return <FormBody key={`${props.tenant?.id ?? "new"}:${String(props.open)}`} {...props} />
}

function FormBody({ open, tenant, onClose }: DeploymentFormProps) {
  const editing = Boolean(tenant)
  const [form] = Form.useForm<Values>()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const [replacing, setReplacing] = useState(false)
  const [weakSecret, setWeakSecret] = useState(0)

  const cron = tenant?.pollCron ?? DEFAULT_POLL_CRON
  /** Daily shape → the time control; otherwise (say, every 6 hours) the raw cron is kept rather than forced into it */
  const dailyTime = cronToTime(cron)
  const timeHint = dailyTime ? localHint(dailyTime) : ""

  const initial: Partial<Values> = {
    ...(tenant
      ? {
          id: tenant.id,
          displayName: tenant.displayName,
          environment: tenant.environment,
          baseUrl: tenant.baseUrl,
          notes: tenant.notes ?? undefined,
        }
      : { environment: "production" }),
    requestTimeoutMs: tenant?.requestTimeoutMs ?? DEFAULT_TIMEOUT_MS,
    ...(dailyTime ? { dailyTime } : { pollCron: cron }),
  }

  const save = useMutation({
    mutationFn: async (v: Values) => {
      const nextCron = v.dailyTime ? timeToCron(v.dailyTime) : v.pollCron
      if (tenant) {
        return updateTenant(tenant.id, {
          displayName: v.displayName,
          environment: v.environment as TenantSummary["environment"],
          baseUrl: v.baseUrl,
          pollCron: nextCron,
          requestTimeoutMs: v.requestTimeoutMs,
          notes: v.notes ?? null,
          ...(replacing && v.secret ? { secret: v.secret } : {}),
        })
      }
      const created = await createTenant({
        id: v.id,
        displayName: v.displayName,
        environment: v.environment as TenantSummary["environment"],
        baseUrl: v.baseUrl,
        secret: v.secret!,
        notes: v.notes,
      })
      if (nextCron !== DEFAULT_POLL_CRON || v.requestTimeoutMs !== DEFAULT_TIMEOUT_MS)
        await updateTenant(v.id, { pollCron: nextCron, requestTimeoutMs: v.requestTimeoutMs })
      return created
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenants"] })
      void message.success(
        editing
          ? "Saved."
          : "Deployment added. Use Probe to check the address and key — collection starts within a minute of the schedule being picked up.",
        6,
      )
      onClose()
    },
    onError: (err) =>
      void message.error(err instanceof ApiError ? err.message : "The request failed.", 8),
  })

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={editing ? `Edit ${tenant!.displayName}` : "Add deployment"}
      okText={editing ? "Save" : "Add"}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      destroyOnHidden
      width={560}
    >
      <Form form={form} layout="vertical" initialValues={initial} onFinish={(v) => save.mutate(v)} style={{ marginTop: 16 }}>
        {!editing && (
          <Form.Item
            name="id"
            label="Deployment id"
            extra="Lowercase letters, digits and hyphens. It never changes and appears in collection logs."
            rules={[
              { required: true },
              {
                pattern: /^[a-z][a-z0-9-]{2,40}$/,
                message: "Lowercase letters, digits and hyphens; 3–41 characters.",
              },
            ]}
          >
            <Input placeholder="acme-prod" autoComplete="off" />
          </Form.Item>
        )}

        <Form.Item name="displayName" label="Name" rules={[{ required: true }]}>
          <Input placeholder="Acme Corp" autoComplete="off" />
        </Form.Item>

        <Form.Item name="environment" label="Environment" rules={[{ required: true }]}>
          <Select options={ENV_ORDER.map((e) => ({ value: e, label: ENV_LABELS[e] }))} />
        </Form.Item>

        <Form.Item
          name="baseUrl"
          label="SRV address"
          extra="No trailing slash. We call <address>/api/metrics/v2 on it."
          rules={[
            { required: true },
            { type: "url", message: "Must be a full URL, e.g. https://acme.example.com" },
            {
              validator: (_, v: string) =>
                v?.endsWith("/") ? Promise.reject(new Error("No trailing slash.")) : Promise.resolve(),
            },
          ]}
        >
          <Input placeholder="https://acme.example.com" autoComplete="off" />
        </Form.Item>

        {/*
          ⚠ The secret is **write-only**: it is encrypted on the way in and no endpoint can read it back.
          So the current value is not shown when editing — it cannot be, and it should not be.
          */}
        {editing && !replacing ? (
          <Form.Item label="Shared key">
            <Typography.Text type="secondary">
              {tenant!.secretSetAt
                ? `Set on ${tenant!.secretSetAt.slice(0, 10)}. It cannot be shown again.`
                : "Held outside the database (environment variable)."}
            </Typography.Text>
            <Button type="link" size="small" onClick={() => setReplacing(true)} style={{ paddingInline: 4 }}>
              Replace key
            </Button>
          </Form.Item>
        ) : (
          <Form.Item
            name="secret"
            label="Shared key"
            extra={
              <>
                Paste the <code>SHARED_SECRET</code> that srv already has. Only generate one for a deployment
                that is being set up now.
              </>
            }
            /**
             * ⚠ Length is **not hard-validated**; short only warns.
             * The secret originates on the srv side and we do not control its length; rejecting a 7-character
             * legacy secret does not make anything stronger, it just makes this deployment impossible to onboard. The judgement belongs to whoever knows the situation.
             */
            rules={[{ required: true }]}
            validateStatus={weakSecret ? "warning" : undefined}
            help={
              weakSecret
                ? `Only ${weakSecret} characters. That is what this srv uses, so it is stored as-is — but it is weak; consider rotating both sides to ${RECOMMENDED_TENANT_SECRET_LENGTH}+ when you can.`
                : undefined
            }
          >
            <Input.Password
              autoComplete="new-password"
              placeholder="Paste it, or generate one →"
              onChange={(e) => {
                const n = e.target.value.length
                setWeakSecret(n > 0 && n < RECOMMENDED_TENANT_SECRET_LENGTH ? n : 0)
              }}
              addonAfter={
                <Button
                  type="link"
                  size="small"
                  icon={<RefreshCw size={12} />}
                  style={{ paddingInline: 4, height: "auto" }}
                  onClick={() => { form.setFieldsValue({ secret: suggestSecret() }); setWeakSecret(0) }}
                >
                  Generate
                </Button>
              }
            />
          </Form.Item>
        )}

        {(!editing || replacing) && (
          /**
           * ⚠ This sentence is not a pleasantry. The secret is **shared by both sides**: we store a copy, and the
           * `SHARED_SECRET` on the customer's srv has to be the same value. Filling in this form **will not make
           * collection succeed** unless the other side matches. Unsaid, someone fills in the form, sees collection fail and assumes the fault is ours.
           */
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Take this key from the deployment — do not change its key"
            description={
              <>
                It must equal <code>SHARED_SECRET</code> on that srv. That value predates us and is also its v1
                metrics key and the HMAC key behind every user pseudonym — changing it there silently renames
                every user, so past and future data stop lining up. Collection fails until both sides match; use
                Probe to check.
              </>
            }
          />
        )}

        {/*
          ⚠ Create and edit offer **the same set of fields**, and none of them collapse.
          cron and timeout used to appear only when editing, an inconsistency with no reason behind it — it made
          people assume "creating uses some value nobody will name".
          ⚠ The control is a time picker, not a cron input. The only shape collection actually uses is "at some
          hour every day", and `17 3 * * *` means nothing to whoever reads it (someone really did ask).
          */}
        {dailyTime !== null ? (
          <Form.Item
            name="dailyTime"
            /**
             * ⚠ The time zone goes in the **label**, not in the help text.
             * Help text is "supplementary, skippable", while "which 03:17 is this" is something you have to know to read the field at all.
             * ⚠ The help text used to say "the default is early morning". That sentence **is usually false for
             * whoever reads it**: 03:17 UTC is 11:17 in the morning at UTC+8. Early morning refers to the
             * *customer's server**, and the sentence never said whose — so the word clashed head-on with the
             * converted local hour beside it. It no longer makes that judgement; it says "pick the customer's quiet hours" and leaves the judgement to whoever knows.
             */
            label="Collection time (UTC)"
            extra={
              <>
                Runs once a day{timeHint && <> · {timeHint}</>}
                <br />
                Pick a time inside this customer&apos;s off-peak hours.
              </>
            }
            rules={[{ required: true, message: "Pick a time." }]}
          >
            <TimePicker format="HH:mm" minuteStep={1} needConfirm={false} allowClear={false} style={{ width: "100%" }} />
          </Form.Item>
        ) : (
          <Form.Item
            name="pollCron"
            label="Collection schedule"
            extra="This deployment uses an advanced cron expression, so it is shown as-is. Times are UTC."
            rules={[{ required: true }]}
          >
            <Input placeholder={DEFAULT_POLL_CRON} />
          </Form.Item>
        )}

        <Form.Item
          name="requestTimeoutMs"
          label="Request timeout (ms)"
          extra="Per HTTP call to that srv. Raise it for deployments with large datasets."
        >
          <InputNumber min={1000} max={120_000} step={1000} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
